import {authConfigurationError} from "../amplify-config";
import React, {useMemo, useState} from "react";
import {
    signIn,
    signUp,
    confirmSignUp,
    fetchAuthSession,
} from "aws-amplify/auth";
import {UserIcon} from "../assets/svgComponents/login/UserIcon";
import {EmailIcon} from "../assets/svgComponents/EmailIcon";
import {PasswordIcon} from "../assets/svgComponents/login/PasswordIcon";
import LoginChatGPTBG from "../assets/images/LoginChatBG.png";
import {FarewiseIconLight} from "../assets/svgComponents/FarewiseIconLight";
import {EyeIcon} from "../assets/svgComponents/login/EyeIcon";
import {EyeClosedIcon} from "../assets/svgComponents/login/EyeClosed";
import {GoogleIcon} from "../assets/svgComponents/socials/GoogleIcon";

/* ---------- Inline icons (no external deps) ---------- */
const IconCheck = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
        <path
            d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Zm-1.1-6.2-3.4-3.4 1.6-1.6 1.8 1.8 4.8-4.8 1.6 1.6-6.4 6.4Z"/>
    </svg>
);
const IconX = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
        <path
            d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Zm-1.4-6 2.8-2.8L18.2 16l1.4-1.4-4.8-4.8 4.6-4.6L18 3.8l-4.6 4.6L8.6 3.8 7.2 5.2l4.6 4.6-4.8 4.8L8.4 16Z"/>
    </svg>
);
const IconApple = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
        <path
            d="M16.6 13.4c0-2.5 2-3.7 2-3.7-1.1-1.7-2.8-1.8-3.4-1.8-1.4-.1-2.8.8-3.5.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.6-.4 6.4 1.1 8.5.8 1.1 1.7 2.3 2.9 2.3 1.1 0 1.5-.7 2.9-.7 1.4 0 1.7.7 2.9.7 1.2 0 2-.9 2.8-2 1-1.4 1.4-2.7 1.4-2.7s-2.7-1.1-2.7-3.8Z"/>
        <path d="M14 5.1c.7-.8 1.2-2 1-3.1-1 .1-2.1.7-2.7 1.5-.6.7-1.2 1.9-1 3 .9.1 2-.5 2.7-1.4Z"/>
    </svg>
);
const IconFb = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="#1877F2" {...p}>
        <path
            d="M22 12.1C22 6.6 17.5 2 12 2S2 6.6 2 12.1C2 17 5.6 21.1 10.3 22v-7h-2.5v-2.9h2.5v-2.2c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6v1.9h2.7L15.6 15H13.5v7C18.4 21.1 22 17 22 12.1Z"/>
    </svg>
);

/* ---------- Small helpers ---------- */
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

type Mode = "login" | "register";

/* ---------- Reusable input with left/right adornments ---------- */
function InputWithIcon({
                           type = "text",
                           value,
                           onChange,
                           placeholder,
                           left,
                           right,
                           "aria-label": ariaLabel,
                       }: {
    type?: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    left: React.ReactNode;
    right?: React.ReactNode;
    "aria-label"?: string;
}) {
    return (
        <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-[#7f7f7f]">
                {left}
            </div>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                aria-label={ariaLabel}
                className="
          h-12 w-full rounded-[12px] border border-[rgba(178,130,229,0.25)]
          bg-white pl-12 pr-12 text-[15px] font-medium text-[#3e3e3e]
          outline-none placeholder:text-[#bfc2cf]
          shadow-[0_1px_0_rgba(113,47,255,0.06)]
          focus:ring-2 focus:ring-[#b282e5]/40
        "
            />
            {right && (
                <div className="absolute inset-y-0 right-3 flex items-center">{right}</div>
            )}
        </div>
    );
}
/* ============================================================= */

const AuthPage: React.FC = () => {
    const [mode, setMode] = useState<Mode>("login");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [revealPw, setRevealPw] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [code, setCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [needsConfirmation, setNeedsConfirmation] = useState(false);

    const emailValid = useMemo(() => isValidEmail(email), [email]);

    async function handleLogin(e: React.FormEvent) {
        e.preventDefault();
        if (authConfigurationError) {
            setError(authConfigurationError);
            return;
        }
        setError(null);
        setLoading(true);
        try {
            await signIn({username: email, password});
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            if (idToken) localStorage.setItem("idToken", idToken);
            window.location.href = window.location.pathname.startsWith("/sessions/")
                ? window.location.pathname : "/sessions";
        } catch (err: any) {
            setError(err?.message || "Login failed");
        } finally {
            setLoading(false);
        }
    }

    async function handleRegister(e: React.FormEvent) {
        e.preventDefault();
        if (authConfigurationError) {
            setError(authConfigurationError);
            return;
        }
        setError(null);
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        setLoading(true);
        try {
            await signUp({
                username: email,
                password,
                options: {userAttributes: {email, name}},
            });
            setNeedsConfirmation(true);
        } catch (err: any) {
            setError(err?.message || "Registration failed");
        } finally {
            setLoading(false);
        }
    }

    async function handleConfirm(e: React.FormEvent) {
        e.preventDefault();
        if (authConfigurationError) {
            setError(authConfigurationError);
            return;
        }
        setError(null);
        setLoading(true);
        try {
            await confirmSignUp({username: email, confirmationCode: code});
            alert("Account confirmed. Please sign in.");
            setMode("login");
            setNeedsConfirmation(false);
        } catch (err: any) {
            setError(err?.message || "Confirmation failed");
        } finally {
            setLoading(false);
        }
    }

    const onSubmit =
        mode === "login" ? handleLogin : needsConfirmation ? handleConfirm : handleRegister;

    return (
        <div
            className="relative min-h-screen font-outfit w-full overflow-hidden bg-gradient-to-b from-[#F6F8FF] to-[#DDE5FF]">
            {/* floating button outside left (just for flair) */}
            <FarewiseIconLight className={"absolute top-20 left-16"}/>

            {/* clouds */}
            {/*<Cloud className="absolute -left-10 bottom-20 w-[220px]" />*/}
            {/*<Cloud className="absolute -right-10 bottom-24 w-[200px]" />*/}

            {/* main card */}
            <div className="flex items-center justify-center px-6 py-16">
                <div
                    className="relative grid grid-cols-1 gap-10 rounded-[26px] bg-white p-8 shadow-[0_25px_80px_rgba(113,47,255,0.12)] md:grid-cols-2 md:p-12">
                    {/* left: form */}
                    <div className="flex flex-col">
                        <h1 className="text-4xl font-extrabold tracking-tight text-[#232323]">
                            {mode === "login" ? "Sign in" : "Sign up"}
                        </h1>

                        {/* segmented control */}
                        <div className="mt-6 flex">
                            <div
                                className="inline-flex rounded-full bg-[#f0f2f8] p-1 shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMode("register");
                                        setNeedsConfirmation(false);
                                    }}
                                    className={`px-4 py-2 text-sm font-semibold rounded-full transition ${
                                        mode === "register"
                                            ? "bg-white shadow text-[#232323]"
                                            : "text-[#7c7f90]"
                                    }`}
                                >
                                    Sign up
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMode("login");
                                        setNeedsConfirmation(false);
                                    }}
                                    className={`px-4 py-2 text-sm font-semibold rounded-full transition ${
                                        mode === "login"
                                            ? "bg-white shadow text-[#232323]"
                                            : "text-[#7c7f90]"
                                    }`}
                                >
                                    Sign in
                                </button>
                            </div>
                        </div>

                        {/* form */}
                        <form onSubmit={onSubmit} className="mt-6 space-y-3">
                            {/* Name (only in register before confirmation) */}
                            {mode === "register" && !needsConfirmation && (
                                <InputWithIcon
                                    value={name}
                                    onChange={setName}
                                    placeholder="Max Mustermann"
                                    aria-label="Name"
                                    left={<UserIcon className={"h-5"}/>}
                                    right={
                                        name ? (
                                            <button
                                                type="button"
                                                onClick={() => setName("")}
                                                className="grid h-6 w-6 place-items-center rounded-full bg-[#e9dff7] text-[#7d52c0] hover:opacity-90"
                                                aria-label="Clear name"
                                            >
                                                <IconX className="h-3.5 w-3.5"/>
                                            </button>
                                        ) : null
                                    }
                                />
                            )}

                            {/* Email */}
                            <InputWithIcon
                                value={email}
                                onChange={setEmail}
                                placeholder="max.mustermann@gmail.com"
                                aria-label="Email"
                                left={<EmailIcon/>}
                                right={
                                    email ? (
                                        emailValid ? (
                                            <span
                                                className="grid h-6 w-6 place-items-center rounded-full bg-[#e5f4ea] text-[#2e7d32]">
                        <IconCheck className="h-3.5 w-3.5"/>
                      </span>
                                        ) : (
                                            <span
                                                className="grid h-6 w-6 place-items-center rounded-full bg-[#f3e3ea] text-[#c62828]">
                        <IconX className="h-3.5 w-3.5"/>
                      </span>
                                        )
                                    ) : null
                                }
                            />

                            {/* Password (hide if confirming) */}
                            {!needsConfirmation && (
                                <InputWithIcon
                                    type={revealPw ? "text" : "password"}
                                    value={password}
                                    onChange={setPassword}
                                    placeholder="Password"
                                    aria-label="Password"
                                    left={<PasswordIcon/>}
                                    right={
                                        <button
                                            type="button"
                                            onClick={() => setRevealPw((v) => !v)}
                                            className="grid h-7 w-7 place-items-center rounded-full  hover:opacity-90"
                                            aria-label={revealPw ? "Hide password" : "Show password"}
                                        >
                                            {revealPw ? (
                                                <EyeClosedIcon className="h-4.5 w-4.5"/>
                                            ) : (
                                                <EyeIcon className="h-4.5 w-4.5"/>
                                            )}
                                        </button>
                                    }
                                />
                            )}

                            {/* Confirm password (register only) */}
                            {mode === "register" && !needsConfirmation && (
                                <InputWithIcon
                                    type="password"
                                    value={confirmPassword}
                                    onChange={setConfirmPassword}
                                    placeholder="Confirm Password"
                                    aria-label="Confirm password"
                                    left={<PasswordIcon/>}
                                    right={
                                        confirmPassword ? (
                                            confirmPassword === password ? (
                                                <span
                                                    className="grid h-6 w-6 place-items-center rounded-full bg-[#e5f4ea] text-[#2e7d32]">
                          <IconCheck className="h-3.5 w-3.5"/>
                        </span>
                                            ) : (
                                                <span
                                                    className="grid h-6 w-6 place-items-center rounded-full bg-[#f3e3ea] text-[#c62828]">
                          <IconX className="h-3.5 w-3.5"/>
                        </span>
                                            )
                                        ) : null
                                    }
                                />
                            )}

                            {/* Confirmation code (after signUp) */}
                            {needsConfirmation && (
                                <InputWithIcon
                                    value={code}
                                    onChange={setCode}
                                    placeholder="Confirmation code"
                                    aria-label="Confirmation code"
                                    left={<PasswordIcon/>}
                                />
                            )}

                            {/* Error */}
                            {(error || authConfigurationError) && (
                                <div className="rounded-md bg-[#ffeaea] px-3 py-2 text-sm font-medium text-[#9b1c1c]">
                                    {error || authConfigurationError}
                                </div>
                            )}

                            {/* Social row text */}
                            <p className="select-none pt-3 text-center text-sm font-medium text-[#a4a9bd]">
                                {mode === "login" ? "Or sign-in with" : "Or sign-up with"}
                            </p>

                            {/* Social buttons */}
                            <div className="mt-2 flex items-center gap-3">
                                <button
                                    type="button"
                                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[12px] bg-white shadow-[0_4px_14px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
                                    aria-label="Continue with Apple"
                                >
                                    <IconApple className="h-5 w-5"/>
                                </button>
                                <button
                                    type="button"
                                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[12px] bg-white shadow-[0_4px_14px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
                                    aria-label="Continue with Google"
                                >
                                    <GoogleIcon className="h-5 w-5"/>
                                </button>
                                <button
                                    type="button"
                                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[12px] bg-white shadow-[0_4px_14px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
                                    aria-label="Continue with Facebook"
                                >
                                    <IconFb className="h-5 w-5"/>
                                </button>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="mt-4 h-12 w-full rounded-[999px] bg-[#b282e5] text-[15px] font-bold text-white shadow-[0_12px_30px_rgba(178,130,229,0.45)] transition-[transform,box-shadow] hover:scale-[1.02] hover:shadow-[0_16px_36px_rgba(178,130,229,0.55)] disabled:opacity-60"
                            >
                                {loading
                                    ? "Loading..."
                                    : mode === "login"
                                        ? "Sign in"
                                        : needsConfirmation
                                            ? "Confirm"
                                            : "Sign up"}
                            </button>

                            {/* Switch */}
                            <p className="pt-2 text-center text-sm">
                                {mode === "login" ? (
                                    <>
                                        No account?{" "}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMode("register");
                                                setNeedsConfirmation(false);
                                            }}
                                            className="font-semibold text-[#7c5acc] underline-offset-2 hover:underline"
                                        >
                                            Sign up
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        Already registered?{" "}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMode("login");
                                                setNeedsConfirmation(false);
                                            }}
                                            className="font-semibold text-[#7c5acc] underline-offset-2 hover:underline"
                                        >
                                            Sign in
                                        </button>
                                    </>
                                )}
                            </p>
                        </form>
                    </div>

                    {/* right: illustration card */}
                    <div className="flex flex-col items-start justify-start gap-4">
                        <div className="hidden md:flex self-end">
                            <FarewiseIconLight className={"h-12"}/>
                        </div>

                        <div
                            className="h-full w-80 rounded-[24px] shadow-[inset_0_0_30px_rgba(255,255,255,0.25),0_30px_60px_rgba(113,47,255,0.18)] md:h-[420px] bg-cover bg-center"
                            style={{backgroundImage: `url(${LoginChatGPTBG})`}}
                        >
                            <div
                                className="relative flex h-full w-full items-end justify-end overflow-hidden rounded-[22px]">
                                <div
                                    className="pointer-events-none absolute inset-0 opacity-[0.12] bg-[radial-gradient(120px_120px_at_20%_25%,rgba(255,255,255,0.85),transparent_60%)]"/>
                                <p className="pb-6 pr-16 text-[18px] font-extrabold tracking-tight text-white/95">
                                    Plan That Trip. Now.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthPage;
